import { Calendar, Dropdown } from "antd";
import {
  CaretDownOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { SvgArrowDown } from "ui/svgs";
import { useEffect, useState, useRef } from "react";
import { calendarDot } from "../../pages/dashboard/schedule/consts";
import { Popover } from "react-tiny-popover";
import { Popover as PopoverAntd } from "antd";
import TimePick from "ui/components/timepick";
import { formatMinute } from "lib/utils";
import { set } from "date-fns";

export default function CustomCalendarRange({
  defaultDate,
  onSectionChange,
  children,
  shifts,
  timeRangeButtonClassname,
}: {
  defaultDate?: dayjs.Dayjs;
  onSectionChange?: (dateS: dayjs.Dayjs, dateE: dayjs.Dayjs) => void;
  children?: React.ReactNode;
  shifts: Map<string, string[]>;
  timeRangeButtonClassname?: string;
}) {
  const containerRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(
    defaultDate ?? dayjs().startOf("day"),
  );
  const [startTimems, setStartTimems] = useState<number>(0);
  const [endTimems, setEndTimems] = useState<number>(24 * 60 * 60 * 1000 - 1);
  const [isSTimePopoverOpen, setIsSTimePopoverOpen] = useState(false);
  const [isETimePopoverOpen, setIsETimePopoverOpen] = useState(false);

  timeRangeButtonClassname =
    timeRangeButtonClassname ||
    "flex flex-grow items-center justify-between rounded-xl hover:border-blue-500 border border-neutral-300 px-5  h-full";

  useEffect(() => {
    if (startTimems > endTimems) {
      setStartTimems(endTimems);
      setEndTimems(startTimems);
      onSectionChange?.(
        dayjs(selectedDate.valueOf() + endTimems),
        dayjs(selectedDate.valueOf() + startTimems),
      );
    } else {
      onSectionChange?.(
        dayjs(selectedDate.valueOf() + startTimems),
        dayjs(selectedDate.valueOf() + endTimems),
      );
    }
  }, [selectedDate, startTimems, endTimems]);

  const calendarCell = (current: dayjs.Dayjs) => {
    const date = current.format("YYYYMMDD");
    const scheduleNumber = (shifts?.get(date) || [])?.length;
    if (!scheduleNumber) {
      return <></>;
    }
    const dots = calendarDot.slice(0, scheduleNumber);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          padding: "0px 1px",
        }}
      >
        {dots.map((color, index) => (
          <div
            key={index}
            style={{
              width: "4px",
              height: "4px",
              display: "inline-block",
              borderRadius: "8px",
              backgroundColor: color,
            }}
          />
        ))}
      </div>
    );
  };

  const calendarHeader = ({ value, type, onChange }) => {
    const renderLabel = (label) => {
      return <span style={{ margin: "5px 0px" }}>{label}</span>;
    };
    const nextMonth = () => {
      setSelectedDate(selectedDate.add(1, "month"));
      onChange(selectedDate.add(1, "month"));
    };
    const previousMonth = () => {
      setSelectedDate(selectedDate.add(-1, "month"));
      onChange(selectedDate.add(-1, "month"));
    };
    const makeYearList = () => {
      const currentYear = dayjs().year();
      const yearList = [];
      for (let year = currentYear - 3; year <= currentYear + 3; year++) {
        yearList.push({
          key: year,
          label: renderLabel(year),
          onClick: ({ item, key }) =>
            setSelectedDate(selectedDate.set("year", key)),
        });
      }
      return yearList;
    };
    return (
      <>
        <div
          ref={containerRef}
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            height: "50px",
            padding: "0px 12px",
          }}
        >
          <Dropdown
            getPopupContainer={() => containerRef.current}
            menu={{
              items: makeYearList(),
            }}
          >
            <div
              style={{ color: "#717171", alignContent: "center" }}
              onClick={(e) => e.preventDefault()}
            >
              {selectedDate.format("MMMM YYYY")}
              <CaretDownOutlined />
            </div>
          </Dropdown>
          <div style={{ alignContent: "center", color: "#717171" }}>
            <LeftOutlined
              style={{ cursor: "pointer" }}
              onClick={previousMonth}
            />
            <RightOutlined
              style={{ marginLeft: "40px", cursor: "pointer" }}
              onClick={nextMonth}
            />
          </div>
        </div>
      </>
    );
  };
  return (
    <>
      <div
        className="border-b border-neutral-200"
        style={{
          height: "20%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          paddingBottom: "24px",
        }}
      >
        <div>
          <div
            style={{
              marginTop: "10%",
              fontFamily: "Open Sans",
              fontSize: "20px",
              fontWeight: 600,
              lineHeight: "27.24px",
              textAlign: "left",
            }}
          >
            {selectedDate.format("MMM DD")}
          </div>
        </div>
        {children}
      </div>
      <div style={{ height: "80%" }}>
        <Calendar
          fullscreen={false}
          headerRender={calendarHeader}
          cellRender={calendarCell}
          onChange={(v) => {
            v.startOf("day");
            setSelectedDate(v);
          }}
          defaultValue={selectedDate}
        />
        <div className="flex justify-between gap-2 items-center">
          <PopoverAntd
            arrow={false}
            overlayClassName="noPaddingPopover"
            getPopupContainer={() => containerRef.current}
            content={
              <div className="border rounded-xl z-[1001] text-[#222222]">
                <TimePick
                  curDateTime={new Date(selectedDate.valueOf() + startTimems)}
                  onChange={(date) => {
                    console.log(date, "date");
                    setStartTimems(date.valueOf() - selectedDate.valueOf());
                  }}
                  minuteInterval={1}
                />
              </div>
            }
          >
            <div className="h-9 flex items-center flex-grow">
              <button
                className={timeRangeButtonClassname}
                onClick={() => setIsSTimePopoverOpen(!isSTimePopoverOpen)}
              >
                <p className="text-sm">
                  {formatMinute(selectedDate.valueOf() + startTimems)}
                </p>
                <SvgArrowDown width="12" className="fill-neutral-400" />
              </button>
            </div>
          </PopoverAntd>
          -
          <PopoverAntd
            overlayClassName="noPaddingPopover"
            getPopupContainer={() => containerRef.current}
            arrow={false}
            content={
              <div className="border rounded-xl z-[1001] text-[#222222]">
                <TimePick
                  curDateTime={new Date(selectedDate.valueOf() + endTimems)}
                  onChange={(date) => {
                    console.log(date, "date");
                    setEndTimems(date.valueOf() - selectedDate.valueOf());
                  }}
                  minuteInterval={1}
                />
              </div>
            }
          >
            <div className="h-9 flex items-center flex-grow">
              <button
                className={timeRangeButtonClassname}
                onClick={() => setIsETimePopoverOpen(!isETimePopoverOpen)}
              >
                <p className="text-sm">
                  {formatMinute(selectedDate.valueOf() + endTimems)}
                </p>
                <SvgArrowDown width="12" className="fill-neutral-400" />
              </button>
            </div>
          </PopoverAntd>
        </div>
      </div>
    </>
  );
}
